import React, { useState } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Lock, Copy, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const TONES = ["Casual", "Professional", "Witty", "Storyteller"];

export default function Humanize() {
  const { user } = useAuth();
  const [tab, setTab] = useState("text");
  const [text, setText] = useState("");
  const [tone, setTone] = useState("Casual");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const isPremium = user?.is_plus || user?.is_pro;
  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
  const limit = isPremium ? 10000 : 50;

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post("/humanize/text", { text, tone });
      setResult(r.data);
    } catch (e) {
      if (e.response?.status === 402) setResult({ locked: true });
    } finally { setLoading(false); }
  };

  return (
    <Layout hideRightPanel>
      <PageHero
        testId="humanize-header"
        eyebrow="Humanize"
        title={<>Make it sound <span className="italic">human.</span></>}
        sub="Rewrite AI-generated text so it reads naturally, or smooth the &quot;AI look&quot; out of generated images. Powered by Claude Sonnet 4.5."
      />

      <div className="flex gap-2 mb-6">
        {["text","image"].map(t => (
          <button key={t} onClick={()=>setTab(t)} className={`px-5 py-2 rounded-full text-sm font-medium ${tab===t ? 'bg-[#0B1220] text-white' : 'btn-ghost'}`} data-testid={`humanize-tab-${t}`}>{t === "text" ? "Text" : "Image"}</button>
        ))}
      </div>

      {tab === "text" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="curio-card p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Paste AI text</h2>
              <div className={`text-xs ${wordCount > limit ? 'text-rose-600' : 'text-[#5B6478]'}`}>{wordCount} / {limit.toLocaleString()} words</div>
            </div>
            <textarea value={text} onChange={e=>setText(e.target.value)} rows={12}
                      placeholder="Paste your AI-written text here…"
                      className="w-full bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#0B1220]"
                      data-testid="humanize-input"/>
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <div className="text-xs uppercase tracking-widest text-[#5B6478] font-bold mr-1">Tone</div>
              {TONES.map(t => (
                <button key={t} onClick={()=>setTone(t)} className={`text-xs px-3 py-1 rounded-full border ${tone===t ? 'bg-[#0B1220] text-white border-[#0B1220]' : 'border-[#E3E8F0]'}`} data-testid={`tone-${t.toLowerCase()}`}>{t}</button>
              ))}
            </div>
            <button onClick={run} disabled={loading || !text.trim()} className="btn-coral mt-4 w-full justify-center disabled:opacity-50" data-testid="humanize-run">
              {loading ? <><Loader2 size={14} className="animate-spin"/> Humanizing…</> : <><Sparkles size={14}/> Humanize</>}
            </button>
          </div>

          <div className="curio-card p-6">
            <h2 className="font-semibold mb-3">Result</h2>
            {result?.locked ? (
              <div className="text-center py-12">
                <Lock size={24} className="mx-auto mb-3 text-[#F97316]"/>
                <div className="font-display text-2xl mb-2">Unlock Humanize</div>
                <p className="text-sm text-[#5B6478] mb-4">Upgrade to Wikifi Creator to humanize up to 10,000 words/mo.</p>
                <Link to="/pricing" className="btn-coral inline-flex">Upgrade to Plus</Link>
              </div>
            ) : result?.humanized ? (
              <div>
                <p className="text-sm whitespace-pre-wrap leading-relaxed mb-3" data-testid="humanize-output">{result.humanized}</p>
                <button onClick={()=>navigator.clipboard.writeText(result.humanized)} className="btn-ghost text-xs"><Copy size={12}/> Copy</button>
              </div>
            ) : (
              <p className="text-sm text-[#5B6478]">Your humanized text will appear here.</p>
            )}
          </div>
        </div>
      )}

      {tab === "image" && (
        <div className="curio-card p-10 text-center">
          {isPremium ? (
            <>
              <Sparkles size={28} className="mx-auto mb-3 text-[#F97316]"/>
              <div className="font-display text-3xl mb-2">Image humanization</div>
              <p className="text-sm text-[#5B6478] mb-4 max-w-md mx-auto">Drop an AI-generated image here to remove the "obvious AI look" — fix skin texture, eye glints, finger geometry, and background coherence.</p>
              <div className="border-2 border-dashed border-[#E3E8F0] rounded-xl p-12 max-w-md mx-auto text-sm text-[#5B6478]">Drag an image here · fal.ai integration scaffolded</div>
            </>
          ) : (
            <>
              <Lock size={28} className="mx-auto mb-3 text-[#F97316]"/>
              <div className="font-display text-3xl mb-2">Unlock image humanization</div>
              <p className="text-sm text-[#5B6478] mb-4">Wikifi Creator members get 20 image humanizations per month.</p>
              <Link to="/pricing" className="btn-coral inline-flex">Upgrade to Plus</Link>
            </>
          )}
        </div>
      )}
    </Layout>
  );
}
